import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dropzone/styles.css";
import { MantineProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { Notifications } from "@mantine/notifications";
import { ModalsProvider } from "@mantine/modals";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { theme } from "./theme.js";
import { useLocale } from "./i18n/use-locale.js";
import { AuthProvider } from "./auth/AuthContext.js";
import { RequireAuth } from "./auth/RequireAuth.js";
import { AppShell } from "./layout/AppShell.js";
import { LoginPage } from "./pages/LoginPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { BedrohungenListPage } from "./pages/bedrohungen/BedrohungenListPage.js";
import { LevelsPage } from "./pages/levels/LevelsPage.js";
import { AssetsListPage } from "./pages/assets/AssetsListPage.js";
import { MassnahmenListPage } from "./pages/massnahmen/MassnahmenListPage.js";
import { EreignisseListPage } from "./pages/ereignisse/EreignisseListPage.js";
import { RisikenListPage } from "./pages/risiken/RisikenListPage.js";
import { AuditLogPage } from "./pages/audit/AuditLogPage.js";
import { UsersListPage } from "./pages/users/UsersListPage.js";
import { DokumentenlenkungPage } from "./pages/docs/DokumentenlenkungPage.js";
import { PublicDocsLayout } from "./pages/docs/PublicDocsLayout.js";
import { DokumentationPage } from "./pages/docs/DokumentationPage.js";
import { DocumentPage } from "./pages/docs/DocumentPage.js";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

export function App() {
  // datePickerFormat() localises the text field, but the calendar popup's
  // month and weekday names come from DatesProvider — whose default locale is
  // the literal string "en" (@mantine/dates DATES_PROVIDER_DEFAULT_SETTINGS).
  // Without this the German UI opened an English calendar.
  const locale = useLocale();
  return (
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <DatesProvider settings={{ locale }}>
        <Notifications position="top-right" />
        <ModalsProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AuthProvider>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  {/*
                    /dokumentation and /dokumentation/:slug are declared ONLY in this
                    block — do not add a second copy of either path inside the
                    RequireAuth block below "for authenticated users". PublicDocsLayout
                    already branches on session state itself (full AppShell chrome once
                    logged in, minimal reader chrome otherwise), so these two routes
                    already serve both audiences.
                    A duplicate would typecheck and build cleanly, and clicking it from
                    a logged-in session would still land on the right-looking page — so
                    it would appear to work. It wouldn't: react-router v6 ranks matching
                    branches by score, and only falls back to declaration order
                    (`compareIndexes`) as a tiebreak between SIBLINGS, i.e. routes under
                    the same parent. A route under RequireAuth and a route under
                    PublicDocsLayout are not siblings, so a same-path duplicate there
                    ties this one's score exactly, compareIndexes returns 0 for that
                    non-sibling pair, and the stable sort just preserves declaration
                    order — leaving this earlier-declared branch matched, always. The
                    duplicate would be permanent dead code with no error, no warning,
                    and no test to catch it.
                  */}
                  <Route element={<PublicDocsLayout />}>
                    <Route path="/dokumentation" element={<DokumentationPage />} />
                    <Route path="/dokumentation/:slug" element={<DocumentPage />} />
                  </Route>
                  <Route element={<RequireAuth><AppShell /></RequireAuth>}>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/bedrohungen" element={<BedrohungenListPage />} />
                    <Route path="/levels" element={<LevelsPage />} />
                    <Route path="/assets" element={<AssetsListPage />} />
                    <Route path="/massnahmen" element={<MassnahmenListPage />} />
                    <Route path="/ereignisse" element={<EreignisseListPage />} />
                    <Route path="/risiken" element={<RisikenListPage />} />
                    <Route path="/audit" element={<AuditLogPage />} />
                    <Route path="/users" element={<UsersListPage />} />
                    <Route path="/dokumentenlenkung" element={<DokumentenlenkungPage />} />
                    <Route path="*" element={<DashboardPage />} />
                  </Route>
                </Routes>
              </AuthProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  );
}
